/**
 * Estate Catalog - rowboat rb.* tables for the MVP slice (spec.md section 90).
 *
 * Lives at the repo root (not under `src/` or `backend/src/`) because both the frontend
 * (`src/schema/estate.ts`) and the backend host need the exact same table definition.
 * Copied convention from the checklist template: ONE definition shared by client + server.
 */
import { type RowOf, rb } from '@jbroll/rowboat-schema';
import { z } from 'zod';

export const Estate = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  name: rb.text(),
  description: rb.text(),
  default_currency: rb.text(),
  status: rb.text(), // planning | active | inventory_complete | ... | archived
  created_by: rb.text(),
  created_at: rb.int(),
  updated_at: rb.int(),
  archived: rb.bool(),
});

export const ItemGroup = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  estate_id: rb.parent('estate'),
  name: rb.text(),
  prefix: rb.text(), // numbering prefix, e.g. LR
  sort_order: rb.int(),
  active: rb.bool(),
  created_at: rb.int(),
  updated_at: rb.int(),
  archived: rb.bool(),
});

export const Category = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  estate_id: rb.parent('estate'),
  name: rb.text(),
  created_at: rb.int(),
  updated_at: rb.int(),
  archived: rb.bool(),
});

export const Item = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  estate_id: rb.parent('estate'),
  item_group_id: rb.ref('item_group'),
  category_id: rb.ref('category'),
  inventory_number: rb.text(),
  number_aliases: rb.json(z.array(z.string())),
  name: rb.text(),
  description: rb.text(),
  condition_text: rb.text(),
  provenance: rb.text(),
  dimensions: rb.text(),
  materials: rb.text(),
  status: rb.text(), // captured | identification | research | appraisal | ...
  created_by: rb.text(),
  created_at: rb.int(),
  updated_at: rb.int(),
  archived: rb.bool(),
});

export const ItemPhoto = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  item_id: rb.parent('item'),
  // null until the bytes upload; rowboat sees blob references only through rb.media() columns
  storage_key: rb.media().nullable(),
  thumbnail_key: rb.media().nullable(),
  role: rb.text(), // overall | detail | signature | ... (optional at capture)
  sort_order: rb.int(),
  created_by: rb.text(),
  created_at: rb.int(),
  archived: rb.bool(),
});

export const Tag = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  estate_id: rb.parent('estate'),
  name: rb.text(),
  archived: rb.bool(),
});

export const ItemTag = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  item_id: rb.parent('item'),
  tag_id: rb.ref('tag'),
  created_at: rb.int(),
  archived: rb.bool(),
});

export const Suggestion = z.object({
  id: rb.id(),
  owner_group_id: rb.scope(),
  item_id: rb.parent('item'),
  field: rb.text(), // name | description | materials | dimensions | condition | category | tag
  value: rb.text(),
  confidence: rb.real(),
  state: rb.text(), // pending | applied | accepted | rejected | superseded
  model: rb.text(),
  photo_ids: rb.json(z.array(z.string())),
  created_at: rb.int(),
  archived: rb.bool(),
});

/**
 * Per-user singleton row (one row whose `id` IS the user's id) holding the sticky
 * capture context: current estate/item group/category. Empty string means "none selected".
 */
export const UserSettings = z.object({
  id: rb.id(), // = the user's id (one singleton row per user)
  owner_group_id: rb.scope(),
  current_estate_id: rb.text(),
  current_item_group_id: rb.text(),
  current_category_id: rb.text(),
});

export const schema = {
  estate: Estate,
  item_group: ItemGroup,
  category: Category,
  item: Item,
  item_photo: ItemPhoto,
  tag: Tag,
  item_tag: ItemTag,
  suggestion: Suggestion,
  user_settings: UserSettings,
};

export type EstateRow = RowOf<typeof Estate>;
export type ItemGroupRow = RowOf<typeof ItemGroup>;
export type CategoryRow = RowOf<typeof Category>;
export type ItemRow = RowOf<typeof Item>;
export type PhotoRow = RowOf<typeof ItemPhoto>;
export type TagRow = RowOf<typeof Tag>;
export type ItemTagRow = RowOf<typeof ItemTag>;
export type SuggestionRow = RowOf<typeof Suggestion>;
export type UserSettingsRow = RowOf<typeof UserSettings>;
